import { IDocument } from '../../Interfaces/IDocument';
import { IInvoiceFullObject } from '../../Interfaces/IInvoice';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalTransactionGwGFullObject } from '../../Interfaces/ILegalTransactionGwG';
import { DocumentDirectoryType } from './documentDirectoryTypes';

/**
 * Ein Eintrag im Inhalt eines Systemordners. Die Dokumente selbst liegen in
 * anderen Bereichen des Vorgangs — hier werden sie nur gespiegelt, deshalb
 * traegt jeder Eintrag zusaetzlich seine Herkunft.
 */
export interface ISystemDirectoryDocument {
  idDocument: number;
  FileName: string;
  idMIMEType: number;
  Created_at: string | null;
  /** Fundstelle im Vorgang, z. B. "Abwicklung / Grundbuchauszug anfordern". */
  origin: string;
}

/**
 * Quellen, aus denen die Systemordner ihren Inhalt spiegeln. Alle Angaben sind
 * optional, damit die Datenablage auch dann funktioniert, wenn ein Bereich
 * (noch) nicht geladen ist.
 */
export interface ISystemDirectorySource {
  legalPhaseArray?: ILegalPhaseFullObject[];
  certificateArray?: IDocument[];
  invoiceArray?: IDocument[];
  invoiceObjectArray?: IInvoiceFullObject[];
  gwgArray?: ILegalTransactionGwGFullObject[];
}

const toMimeType = (value: number | string | undefined | null): number => {
  const parsed = typeof value === 'string' ? Number(value) : value;

  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : 1;
};

const toEntry = (
  documentObject: Partial<IDocument> | undefined,
  origin: string
): ISystemDirectoryDocument | null => {
  if (
    documentObject === undefined ||
    typeof documentObject.idDocument !== 'number' ||
    documentObject.idDocument <= 0
  ) {
    return null;
  }

  return {
    idDocument: documentObject.idDocument,
    FileName:
      documentObject.FileName ?? `Dokument ${documentObject.idDocument}`,
    idMIMEType: toMimeType(documentObject.idMIMEType),
    Created_at: documentObject.Created_at ?? null,
    origin: origin,
  };
};

const uniqueByIdDocument = (
  entryArray: ISystemDirectoryDocument[]
): ISystemDirectoryDocument[] =>
  Array.from(
    new Map(entryArray.map(entry => [entry.idDocument, entry])).values()
  );

const getRequirementDocuments = (
  source: ISystemDirectorySource
): ISystemDirectoryDocument[] => {
  const entryArray: ISystemDirectoryDocument[] = [];

  (source.legalPhaseArray ?? []).forEach(legalPhase => {
    (legalPhase.LegalRequirementArray ?? []).forEach(legalRequirement => {
      const origin = `${legalPhase.LegalPhase} / ${legalRequirement.LegalRequirement}`;

      [
        ...(legalRequirement.DocumentArrayEmployees ?? []),
        ...(legalRequirement.DocumentArrayClient ?? []),
      ].forEach(documentObject => {
        const entry = toEntry(documentObject, origin);

        if (entry !== null) {
          entryArray.push(entry);
        }
      });
    });
  });

  return uniqueByIdDocument(entryArray);
};

const getCertificateDocuments = (
  source: ISystemDirectorySource
): ISystemDirectoryDocument[] =>
  uniqueByIdDocument(
    (source.certificateArray ?? [])
      .map(documentObject => toEntry(documentObject, 'Urkunden'))
      .filter((entry): entry is ISystemDirectoryDocument => entry !== null)
  );

const getInvoiceDocuments = (
  source: ISystemDirectorySource
): ISystemDirectoryDocument[] => {
  const entryArray: ISystemDirectoryDocument[] = [];

  (source.invoiceArray ?? []).forEach(documentObject => {
    const entry = toEntry(documentObject, 'Rechnungen');

    if (entry !== null) {
      entryArray.push(entry);
    }
  });

  (source.invoiceObjectArray ?? []).forEach(invoiceObject => {
    const entry = toEntry(
      {
        idDocument: invoiceObject.idDocument,
        FileName: invoiceObject.FileName,
        idMIMEType: invoiceObject.idMIMEType,
        Created_at: invoiceObject.Created_at,
      },
      invoiceObject.InvoiceNumber === ''
        ? 'Rechnungen'
        : `Rechnung ${invoiceObject.InvoiceNumber}`
    );

    if (entry !== null) {
      entryArray.push(entry);
    }
  });

  return uniqueByIdDocument(entryArray);
};

const getGwGDocuments = (
  source: ISystemDirectorySource
): ISystemDirectoryDocument[] => {
  const entryArray: ISystemDirectoryDocument[] = [];

  (source.gwgArray ?? []).forEach(gwgObject => {
    (gwgObject.LegalTransactionGwGEntryArray ?? []).forEach(gwgEntry => {
      const origin =
        gwgEntry.title === null || gwgEntry.title === ''
          ? 'GwG-Nachweise'
          : `GwG: ${gwgEntry.title}`;

      (gwgEntry.DocumentArray ?? []).forEach(documentObject => {
        const entry = toEntry(documentObject, origin);

        if (entry !== null) {
          entryArray.push(entry);
        }
      });
    });
  });

  return uniqueByIdDocument(entryArray);
};

/**
 * Ermittelt den gespiegelten Inhalt eines Systemordners. Fuer Standardordner
 * wird ein leeres Array geliefert — deren Inhalt kommt aus der Datenablage
 * selbst.
 */
export const getSystemDirectoryDocuments = (
  directoryType: DocumentDirectoryType,
  source: ISystemDirectorySource
): ISystemDirectoryDocument[] => {
  switch (directoryType) {
    case 'requirement':
      return getRequirementDocuments(source);
    case 'certificate':
      return getCertificateDocuments(source);
    case 'invoice':
      return getInvoiceDocuments(source);
    case 'gwg':
      return getGwGDocuments(source);
    default:
      return [];
  }
};
